import {
  getBrowserDefaultLanguage,
  getBrowserDefaultLocaleKey,
} from "@faire/retailer-visitor-shared/consts/LANGUAGE";
import { getLocaleCountryUrlPrefix } from "@faire/retailer-visitor-shared/serialized-data/getLocaleCountryUrlPrefix";
import { getUser } from "@faire/retailer-visitor-shared/serialized-data/getUser";
import { useSettingUserStoreHooksMigration } from "@faire/retailer-visitor-shared/settings/useSettingUserStoreHooksMigration";
import { DEFAULT_LOCALE_KEY } from "@faire/web--source/common/consts/DEFAULT_LOCALE";
import { SHOP_PREVIEW_OVERRIDE_KEY } from "@faire/web--source/common/consts/SHOP_PREVIEW_OVERRIDE_KEY";
import { localeStringToLocaleKey } from "@faire/web--source/common/consts/TWO_TO_THREE_LETTER_LANGUAGE_CODE";
import { getCookies } from "@faire/web--source/common/cookies";
import { useCurrencyStore } from "@faire/web--source/common/currency/CurrencyStore";
import { getLocationOrThrow } from "@faire/web--source/common/globals/getLocation";
import { faireBaseUrl } from "@faire/web--source/common/routes/faireBaseUrl";
import { getLocaleKey } from "@faire/web--source/common/serialized-data/getLocaleKey";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { useQuery_escape_hatch as useAddressesQuery } from "@faire/web-api--source/endpoints/www/api/retailer/address/list/post-hooks";
import {
  useQuery as useUserQuery,
  getQueryKey as getUserQueryKey,
} from "@faire/web-api--source/endpoints/www/api/user/get-hooks";
import logout from "@faire/web-api--source/endpoints/www/api/user/logout/post";
import type { CapabilityEnum } from "@faire/web-api--source/faire/session/CapabilityEnum";
import { MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS } from "@faire/web-api--source/faire/session/CapabilityEnum/MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS";
import { RETAILER_PLACE_ORDER } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_PLACE_ORDER";
import { RETAILER_TRANSFER_OWNERSHIP } from "@faire/web-api--source/faire/session/CapabilityEnum/RETAILER_TRANSFER_OWNERSHIP";
import { IAddress } from "@faire/web-api--source/indigofair/data/IAddress";
import { IListRetailerAddressesRequest } from "@faire/web-api--source/indigofair/data/IListRetailerAddressesRequest";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { IRetailerAccountUserManagement } from "@faire/web-api--source/indigofair/retailer/IRetailerAccountUserManagement";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";

import { IUserStore } from "./IUserStore";
import { useUserStore as useOldUserStore } from "./UserStore";

export const useNewUserStore = (_?: unknown): IUserStore => {
  const { setCurrency } = useCurrencyStore(["setCurrency"]);

  const { data: user, refetch: refetchUser } = useUserQuery({
    refetchOnMount: false,
    staleTime: Infinity,
    initialData: getUser(),
  });

  const queryClient = useQueryClient();
  const setUser = useCallback(
    (user: IUser | undefined) => {
      queryClient.setQueryData(getUserQueryKey(), user);
    },
    [queryClient]
  );

  const patchUser = useCallback(
    (_user: Partial<IUser>) => {
      queryClient.setQueryData(
        getUserQueryKey(),
        IUser.build({
          ...user,
          ..._user,
        })
      );
    },
    [queryClient, user]
  );

  const { data: addresses, refetch: fetchUserAddresses } = useAddressesQuery(
    IListRetailerAddressesRequest.build({}),
    {
      refetchOnMount: false,
      staleTime: Infinity,
    }
  );

  const fetchUser = useCallback(async () => {
    refetchUser();
    fetchUserAddresses();
  }, [refetchUser, fetchUserAddresses]);

  const userAddresses = useMemo(
    () =>
      (addresses?.addresses ?? [])
        .map((address) => address.address)
        .filter(isNotUndefined),
    [addresses]
  );

  const addressDeletableMap: Record<string, boolean> = useMemo(
    () =>
      (addresses?.addresses ?? []).reduce((acc, address) => {
        if (!address.address?.token) {
          return acc;
        }
        return {
          ...acc,
          [address.address.token]: !!address.is_deletable,
        };
      }, {}),
    [addresses]
  );

  const userToken = useMemo(() => user?.token ?? "", [user?.token]);

  const fullName = useMemo(() => {
    if (!user?.first_name || !user?.last_name) {
      return "";
    }
    return [user.first_name, user.last_name].join(" ").trim();
  }, [user]);

  const firstName = useMemo(() => user?.first_name ?? "", [user?.first_name]);
  const lastName = useMemo(() => user?.last_name ?? "", [user?.last_name]);
  const emailAddress = user?.email_address;
  const phoneNumber = user?.phone_number;
  const defaultAddress = useMemo(
    () => user?.default_shipping_address ?? IAddress.build(),
    [user?.default_shipping_address]
  );
  const isEmployee = !!user?.is_employee;
  const isImpersonating = !!user?.impersonating;

  const userLocale = useMemo(
    () =>
      user?.locale_key?.language ??
      getBrowserDefaultLanguage() ??
      DEFAULT_LOCALE_KEY.language,
    [user?.locale_key?.language]
  );

  const browserLevelLanguage = useMemo(
    () => getLocaleKey()?.language ?? userLocale,
    [userLocale]
  );

  const userLocaleKey = useMemo(
    () =>
      user?.locale_key ?? getBrowserDefaultLocaleKey() ?? DEFAULT_LOCALE_KEY,
    [user?.locale_key]
  );

  const browserLevelLocaleKey = useMemo(
    () => getLocaleKey() ?? userLocaleKey,
    [userLocaleKey]
  );

  const browserLevelLocaleOverride = useMemo(() => {
    const overrideLanguage = getCookies(SHOP_PREVIEW_OVERRIDE_KEY);
    if (
      !overrideLanguage ||
      !localeStringToLocaleKey(overrideLanguage).language
    ) {
      return undefined;
    }
    return localeStringToLocaleKey(overrideLanguage);
  }, []);

  const hasFaireEmailAddress = useMemo(
    () => !!emailAddress?.endsWith("@faire.com"),
    [emailAddress]
  );

  const userCapabilities = useMemo(() => user?.capabilities ?? [], [user]);

  const userHasSufficientCapability = useCallback(
    (capability: CapabilityEnum): boolean => {
      return userCapabilities.includes(capability);
    },
    [userCapabilities]
  );

  const isRetailerSubUser = useMemo(
    () => !userHasSufficientCapability(RETAILER_TRANSFER_OWNERSHIP),
    [userHasSufficientCapability]
  );

  const isOwnerUser = useMemo(
    () => userHasSufficientCapability(RETAILER_TRANSFER_OWNERSHIP),
    [userHasSufficientCapability]
  );

  const accountType = useMemo(() => {
    if (!isRetailerSubUser) {
      return IRetailerAccountUserManagement.Role.OWNER;
    }
    return userHasSufficientCapability(MANAGE_RETAILER_ACCOUNT_USER_PERMISSIONS)
      ? IRetailerAccountUserManagement.Role.ADMIN
      : IRetailerAccountUserManagement.Role.CONTRIBUTOR;
  }, [isRetailerSubUser, userHasSufficientCapability]);

  const userCanPlaceOrder = useMemo(
    () => userCapabilities.includes(RETAILER_PLACE_ORDER),
    [userCapabilities]
  );

  const userProfileImage = user?.profile_image;

  const isBrandUser = user?.type === IUser.Type.BRAND_USER;

  const isAdminUser = useMemo(() => user?.type === IUser.Type.ADMIN, [user]);

  const addressIsDeletable = useCallback(
    (addressToken: string): boolean => {
      if (!addressToken) {
        return false;
      }
      return !!addressDeletableMap[addressToken];
    },
    [addressDeletableMap]
  );

  const logOut = useCallback(
    async (dest?: string) => {
      await logout();
      setCurrency(undefined);
      getLocationOrThrow().href = dest ? dest : faireBaseUrl();
    },
    [setCurrency]
  );

  const localeCountryUrlPrefix = useMemo(() => getLocaleCountryUrlPrefix(), []);

  return {
    user,
    /**
     * @deprecated Use callback from useSetUser instead
     */
    setUser,
    patchUser,
    /**
     * @deprecated Use callback from useFetchUser instead
     */
    fetchUser,
    userAddresses,
    addresses: userAddresses,
    fetchUserAddresses,
    userToken,
    fullName,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    defaultAddress,
    isEmployee,
    isImpersonating,
    browserLevelLanguage,
    userLocaleKey,
    browserLevelLocaleKey,
    browserLevelLocaleOverride,
    hasFaireEmailAddress,
    isRetailerSubUser,
    isOwnerUser,
    accountType,
    userCanPlaceOrder,
    userProfileImage,
    isBrandUser,
    isAdminUser,
    userHasSufficientCapability,
    addressIsDeletable,
    logOut,
    localeCountryUrlPrefix,
  };
};

export const useUserStore = (fields: (keyof IUserStore)[]): IUserStore => {
  const setting = useSettingUserStoreHooksMigration();
  if (setting) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useNewUserStore();
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useOldUserStore(fields);
};
